'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './NeumoArticlesExplorer.module.scss';
import {
  IconCategory,
  IconCheck,
  IconLayoutGrid,
  IconSearch,
  IconSortDescending,
  IconTag,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { Input, Pagination } from '@mantine/core';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { fetchArticles } from '@/src/helpers/fetch-articles';

type Props = {
  lang: 'fr' | 'en';
  initialData?: {
    articles: Article[];
    totalPages: number;
  };
};

type ViewMode = 'filter' | 'sections';
type SortMode = 'newest' | 'az';

interface Article {
  id: string;
  title: any;
  tags: string[];
  mainImage: string;
  createdAt: string;
  description: any;
  author?: string;
  category?: string;
}

function getLocalizedText(val: any, lang: 'fr' | 'en') {
  if (!val) {
    return '';
  }
  if (typeof val === 'string') {
    return val;
  }
  return val?.[lang] ?? val?.fr ?? val?.en ?? '';
}

function normalizeCategory(cat?: string) {
  const c = (cat || '').trim();
  return c || 'default';
}

function prettifyCategory(cat: string, pageCopy: any) {
  if (cat === 'default') {
    return pageCopy.defaultCategoryLabel;
  }
  return cat.replaceAll('_', ' ').replaceAll('-', ' ');
}

function makeDbSafeSlugFromTitle(title: string) {
  return (title || '').trim().replaceAll('-', '_').replace(/\s+/g, '-');
}

function normalizeInitialData(data?: Props['initialData'] | Article[]): {
  articles: Article[];
  totalPages: number;
} {
  if (!data) {
    return { articles: [], totalPages: 1 };
  }
  if (Array.isArray(data)) {
    return { articles: data as Article[], totalPages: 1 };
  }
  return {
    articles: (data as Props['initialData'])?.articles ?? [],
    totalPages: (data as Props['initialData'])?.totalPages ?? 1,
  };
}

export default function NeumoArticlesExplorer({ lang, initialData }: Props) {
  const dictionary = useDictionary();
  const pageCopy = dictionary.pages.articles;

  const normalizedInitial = normalizeInitialData(initialData);
  const [articles, setArticles] = useState<Article[]>(normalizedInitial.articles);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(normalizedInitial.totalPages);

  const [viewMode, setViewMode] = useState<ViewMode>('filter');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const listRef = useRef<HTMLDivElement | null>(null);

  const hasFetchedInitial = useRef(Boolean(initialData));

  useEffect(() => {
    if (page === 1 && hasFetchedInitial.current) {
      hasFetchedInitial.current = false;
      return;
    }

    const run = async () => {
      const res: any = await fetchArticles(
        page,
        searchQuery,
        lang,
        undefined,
        selectedCategory === 'all' ? undefined : selectedCategory
      );

      setArticles(res.articles ?? []);
      setTotalPages(res.totalPages ?? 1);
    };

    run();
  }, [page, searchQuery, lang, selectedCategory]);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(articles.map((a: any) => normalizeCategory(a.category))));
    return cats.sort((a, b) =>
      prettifyCategory(a, pageCopy).localeCompare(prettifyCategory(b, pageCopy), lang)
    );
  }, [articles, pageCopy, lang]);

  useEffect(() => {
    if (selectedCategory !== 'all' && categories.length && !categories.includes(selectedCategory)) {
      setSelectedCategory('all');
    }
  }, [categories, selectedCategory]);

  const sortedArticles = useMemo(() => {
    const list = [...articles];

    if (sortMode === 'newest') {
      list.sort((a: any, b: any) => {
        const da = new Date(a.createdAt || 0).getTime();
        const db = new Date(b.createdAt || 0).getTime();
        return db - da;
      });
    } else {
      list.sort((a: any, b: any) => {
        const ta = getLocalizedText(a.title, lang).toLowerCase();
        const tb = getLocalizedText(b.title, lang).toLowerCase();
        return ta.localeCompare(tb, lang);
      });
    }

    return list;
  }, [articles, sortMode, lang]);

  const filteredArticles = useMemo(() => {
    return selectedCategory === 'all'
      ? sortedArticles
      : sortedArticles.filter((a: any) => normalizeCategory(a.category) === selectedCategory);
  }, [sortedArticles, selectedCategory]);

  const groupedByCategory = useMemo(() => {
    const map = new Map<string, Article[]>();
    for (const a of sortedArticles) {
      const key = normalizeCategory((a as any).category);
      map.set(key, [...(map.get(key) || []), a]);
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      prettifyCategory(a, pageCopy).localeCompare(prettifyCategory(b, pageCopy), lang)
    );
  }, [sortedArticles, pageCopy, lang]);

  const featuredArticles = useMemo(() => {
    return [...articles]
      .sort((a: any, b: any) => {
        const da = new Date(a.createdAt || 0).getTime();
        const db = new Date(b.createdAt || 0).getTime();
        return db - da;
      })
      .slice(0, 3);
  }, [articles]);

  const clearSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  const resultsCount = viewMode === 'filter' ? filteredArticles.length : sortedArticles.length;

  const trustItems = [
    pageCopy.trustReply,
    pageCopy.trustNoCommitment,
    pageCopy.trustTailored,
  ].filter(Boolean);

  const titleMarkup = (text?: string) => {
    if (!text) {
      return null;
    }
    const parts = text.split('*');
    return parts.map((part, i) =>
      i % 2 === 1 ? <em key={i}>{part}</em> : <React.Fragment key={i}>{part}</React.Fragment>
    );
  };

  return (
    <section className={styles.explorer} id="articles-list" aria-labelledby="articles-title">
      <div className={styles.heroBanner}>
        <div className={styles.container}>
          <div className={styles.blockHeader}>
            <p className={styles.eyebrow}>{pageCopy.listEyebrow}</p>
            <h1 id="articles-title" className={styles.blockTitle}>
              {titleMarkup(pageCopy.listTitle)}
            </h1>
            <p className={styles.blockSubtitle}>{pageCopy.listSubtitle}</p>

            {trustItems.length > 0 && (
              <div className={styles.trustBar}>
                {trustItems.map((item, index) => (
                  <div key={index} className={styles.trustItem}>
                    <span className={styles.trustIcon} aria-hidden="true">
                      <IconCheck size={14} />
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.container}>
        {featuredArticles.length > 0 && (
          <section className={styles.featured} aria-label={pageCopy.featuredTitle}>
            <div className={styles.featuredHeader}>
              <p className={styles.featuredEyebrow}>{pageCopy.featuredTitle}</p>
              <p className={styles.featuredSubtitle}>{pageCopy.featuredSubtitle}</p>
            </div>
            <div className={styles.featuredGrid} role="list">
              {featuredArticles.map((a, index) => (
                <ArticleCard
                  key={a.id || index}
                  article={a}
                  lang={lang}
                  pageCopy={pageCopy}
                  featured={index === 0}
                  role="listitem"
                />
              ))}
            </div>
          </section>
        )}

        <section className={styles.realizations} aria-label={pageCopy.listAriaLabel}>
          <div ref={listRef} />

          <div className={styles.browseHeader}>
            <h2 className={styles.browseTitle}>{pageCopy.browseTitle}</h2>
            <span className={styles.resultsInfo}>
              <span className={styles.resultsNumber}>{resultsCount}</span>
              <span>{pageCopy.resultsLabel}</span>
            </span>
          </div>

          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <div className={styles.searchRow}>
                <Input
                  placeholder={pageCopy.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={18} />}
                  classNames={{ input: styles.searchInput }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className={styles.clearBtn}
                    onClick={clearSearch}
                    aria-label={pageCopy.clearSearchAriaLabel}
                    title={pageCopy.clearSearchTitle}
                  >
                    <IconX size={18} />
                  </button>
                )}
              </div>

              <div
                className={styles.chipsRow}
                role="list"
                aria-label={pageCopy.categoriesAriaLabel}
              >
                <button
                  type="button"
                  className={`${styles.chip} ${selectedCategory === 'all' ? styles.chipActive : ''}`}
                  onClick={() => {
                    setSelectedCategory('all');
                    setPage(1);
                  }}
                >
                  {pageCopy.allLabel}
                </button>

                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`${styles.chip} ${selectedCategory === cat ? styles.chipActive : ''}`}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setPage(1);
                      setViewMode('filter');
                    }}
                  >
                    {prettifyCategory(cat, pageCopy)}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.toolbarRight}>
              <div className={styles.pills}>
                <button
                  type="button"
                  className={`${styles.pill} ${viewMode === 'filter' ? styles.pillActive : ''}`}
                  onClick={() => setViewMode('filter')}
                  aria-pressed={viewMode === 'filter'}
                >
                  <IconLayoutGrid size={16} />
                  <span>{pageCopy.viewFilterLabel}</span>
                </button>

                <button
                  type="button"
                  className={`${styles.pill} ${viewMode === 'sections' ? styles.pillActive : ''}`}
                  onClick={() => {
                    setViewMode('sections');
                    setSelectedCategory('all');
                  }}
                  aria-pressed={viewMode === 'sections'}
                >
                  <IconCategory size={16} />
                  <span>{pageCopy.viewSectionsLabel}</span>
                </button>

                <button
                  type="button"
                  className={styles.pill}
                  onClick={() => setSortMode((s) => (s === 'newest' ? 'az' : 'newest'))}
                  aria-label={pageCopy.sortToggleAriaLabel}
                  title={pageCopy.sortToggleTitle}
                >
                  <IconSortDescending size={16} />
                  <span>
                    {sortMode === 'newest' ? pageCopy.sortNewestLabel : pageCopy.sortAzLabel}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className={styles.content}>
            {viewMode === 'filter' ? (
              <>
                <div
                  className={styles.grid}
                  role="list"
                  aria-label={pageCopy.articlesListAriaLabel}
                >
                  {filteredArticles.map((a) => (
                    <ArticleCard
                      key={a.id}
                      article={a}
                      lang={lang}
                      pageCopy={pageCopy}
                      role="listitem"
                    />
                  ))}
                </div>

                <div className={styles.paginationRow}>
                  <Pagination
                    value={page}
                    onChange={setPage}
                    total={totalPages}
                    radius="xl"
                    color="dark"
                    aria-label="Pagination"
                    getItemProps={(p) => ({
                      'aria-label': `Go to page ${p}`,
                    })}
                    getControlProps={(control) => ({
                      'aria-label':
                        control === 'next'
                          ? 'Next page'
                          : control === 'previous'
                            ? 'Previous page'
                            : control === 'first'
                              ? 'First page'
                              : 'Last page',
                    })}
                  />
                </div>
              </>
            ) : (
              <div className={styles.sections}>
                {groupedByCategory.map(([cat, items]) => (
                  <section
                    key={cat}
                    className={styles.section}
                    aria-label={prettifyCategory(cat, pageCopy)}
                  >
                    <div className={styles.sectionHeader}>
                      <h3 className={styles.sectionTitle}>{prettifyCategory(cat, pageCopy)}</h3>
                      <p className={styles.sectionHint}>{pageCopy.sectionHint}</p>
                    </div>

                    <div
                      className={styles.grid}
                      role="list"
                      aria-label={pageCopy.articlesListAriaLabel}
                    >
                      {items.map((a) => (
                        <ArticleCard
                          key={a.id}
                          article={a}
                          lang={lang}
                          pageCopy={pageCopy}
                          role="listitem"
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </section>
  );
}

function ArticleCard({
  article,
  lang,
  pageCopy,
  featured = false,
  role,
}: {
  article: Article;
  lang: 'fr' | 'en';
  pageCopy: any;
  featured?: boolean;
  role?: string;
}) {
  const title = getLocalizedText(article?.title, lang);
  const description = getLocalizedText(article?.description, lang);
  const slug = makeDbSafeSlugFromTitle(title || pageCopy.fallbackSlugLabel);
  const href = `/${lang}/articles/${encodeURIComponent(slug)}`;

  const date = article?.createdAt
    ? new Date(article.createdAt).toLocaleDateString(pageCopy.dateLocale)
    : '';

  const category = prettifyCategory(normalizeCategory(article?.category), pageCopy);

  return (
    <article className={`${styles.card} ${featured ? styles.cardFeatured : ''}`} role={role}>
      <Link href={href} className={styles.cardLink} aria-label={title}>
        <div className={styles.media}>
          <Image
            src={article?.mainImage ?? ''}
            alt={title}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            style={{ objectFit: 'cover' }}
            priority={featured}
          />
          <span className={styles.badge}>{category}</span>
        </div>

        <div className={styles.cardBody}>
          <h3 className={featured ? styles.featuredCardTitle : styles.cardTitle}>{title}</h3>
          <p className={styles.cardDesc}>{description}</p>

          <div className={styles.cardBottom}>
            <div className={styles.metaLeft}>
              {article?.author ? (
                <span className={styles.metaItem}>
                  <IconUser size={14} />
                  {article.author}
                </span>
              ) : null}
              {date ? <span className={styles.metaItem}>· {date}</span> : null}
            </div>

            <span className={styles.cardCta}>{pageCopy.cardCtaLabel}</span>
          </div>

          {Array.isArray(article?.tags) && article.tags.length ? (
            <div className={styles.tags}>
              <IconTag size={14} />
              <span>
                {pageCopy.tagsPrefixLabel}{' '}
                {article.tags.slice(0, pageCopy.tagsMaxCount).join(pageCopy.tagsSeparator)}
              </span>
            </div>
          ) : null}
        </div>
      </Link>
    </article>
  );
}
